import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../redux/slices/auth';
import { useSelector } from 'react-redux';
import {
  useCreateGiftMutation,
  useUpdateGiftMutation,
  usePrepareTransactionsMutation,
  useBroadcastAndTrackMutation,
  useGetGiftQuery,
  useGetGiftsQuery,
  useCreateTrialGiftMutation,
} from '../../redux/api/authorized/gifts';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../redux/slices/tokenSlice';
import { toTokenAmount } from '../../hooks/useSolana';
import { useSolana } from '../../hooks/useSolana';
import { useGiftAmountCalculations } from '../../hooks/useGiftAmountCalculations';
import {
  EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED,
  EVENT_GIFT_FLOW_GIFT_CREATED,
  EVENT_GIFT_FLOW_GIFT_FAILED,
  EVENT_FREE_GIFT_FLOW_SHOWN,
  sendEvent,
} from '../../common/analytics';
import { hapticsImpactMedium } from '../../common/haptic';
import Customize from '../GiftFlow/components/Customize';
import AmountSelector from '../GiftFlow/components/AmountSelector';
import ShippingAddress from '../GiftFlow/components/ShippingAddress';
import SenderInfo from '../GiftFlow/components/SenderInfo';
import Confirmation from '../GiftFlow/components/Confirmation';
import TokenSelector from '../GiftFlow/components/TokenSelector';
import { useFlag } from '@unleash/proxy-client-react';
import { getVerisoulSession } from '../../common/verisoul';
import ConfirmationSkeleton from '../GiftFlow/components/Confirmation/skeleton.jsx';

const giftFormSchema = yup
  .object({
    delivery: yup
      .string()
      .oneOf(['email', 'link'], 'Delivery method must be email or link')
      .required('Delivery method is required'),
    isUsingUSDC: yup.boolean(),
    userEmail: yup.string(),
    email: yup.string().when('delivery', {
      is: 'email',
      then: (schema) =>
        schema
          .email('Must be a valid email')
          .required('Email is required')
          .test(
            'email-match',
            'You cannot use your own email',
            (value, context) => value !== context.parent.userEmail,
          ),
      otherwise: (schema) => schema.optional(),
    }),
    message: yup
      .string()
      .optional()
      .max(190, 'Message must be at most 190 characters'),
    senderName: yup
      .string()
      .optional()
      .max(32, 'From field must be at most 40 characters'),
    image: yup.string().required('Card image is required'),
    network: yup.string().required('Network is required'),
    targetTokenAddress: yup.string().required('Token address is required'),
    recipientName: yup.string(),
    addressLine1: yup.string(),
    addressLine2: yup.string(),
    city: yup.string(),
    formattedAddress: yup.string(),
    state: yup.string(),
    postalCode: yup.string(),
    country: yup.string(),
    usdAmount: yup.string().when('isUsingUSDC', {
      is: true,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Minimum amount is 1', (value) => {
            const n = Number(value);
            return !isNaN(n) && n >= 1;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    targetTokenAmount: yup.string().when('isUsingUSDC', {
      is: false,
      then: (schema) =>
        schema
          .required('Amount is required')
          .test('is-number', 'Must be a valid number', (value) =>
            value === '' || value == null ? false : !isNaN(Number(value)),
          )
          .test('min-1', 'Must be greater than 0', (value) => {
            const n = Number(value);
            return !isNaN(n) && n > 0;
          }),
      otherwise: (schema) => schema.optional(),
    }),
    shippingAddressId: yup.number().when('delivery', {
      is: 'mail',
      then: (schema) => schema.required('Shipping address is required'),
      otherwise: (schema) => schema.optional(),
    }),
  })
  .required();

const GiftFormContext = createContext(null);

const defaultInitialValues = {
  isUsingUSDC: true,
  delivery: 'link',
  usdAmount: '1',
  targetTokenAmount: '',
};

// Step names - using strings for URL readability
export const STEP_NAMES = {
  TOKEN: 'token',
  CUSTOMIZE: 'customize',
  AMOUNT: 'amount',
  SENDER_INFO: 'sender-info',
  SHIPPING: 'shipping',
  CONFIRMATION: 'confirmation',
};

// Define components at module level - STABLE REFERENCES
const STEP_COMPONENTS = {
  TokenSelector,
  Customize,
  AmountSelector,
  SenderInfo,
  ShippingAddress,
  Confirmation,
};

const buildSteps = (
  delivery,
  initialIsAuthenticated,
  showFreeGift,
  freeGiftAllInputs,
) => {
  const steps = [
    {
      name: STEP_NAMES.TOKEN,
      component: STEP_COMPONENTS.TokenSelector,
      title: 'Select Asset',
      buttonLabel: 'Continue',
    },
    {
      name: STEP_NAMES.CUSTOMIZE,
      component: STEP_COMPONENTS.Customize,
      title: 'Customize Gift',
      buttonLabel: 'Continue',
    },
  ];

  if (!showFreeGift || (showFreeGift && freeGiftAllInputs)) {
    steps.push({
      name: STEP_NAMES.AMOUNT,
      component: STEP_COMPONENTS.AmountSelector,
      title: 'Select Amount',
      buttonLabel: initialIsAuthenticated
        ? delivery === 'mail'
          ? 'Shipping Details'
          : 'Checkout'
        : 'Continue',
    });
  }

  // Only include SenderInfo step when explicitly enabled (progressive onboarding)
  if (!initialIsAuthenticated) {
    steps.push({
      name: STEP_NAMES.SENDER_INFO,
      component: STEP_COMPONENTS.SenderInfo,
      title: 'Sender Info',
      buttonLabel: delivery === 'mail' ? 'Shipping Details' : 'Checkout',
    });
  }

  if (delivery === 'mail') {
    steps.push({
      name: STEP_NAMES.SHIPPING,
      component: STEP_COMPONENTS.ShippingAddress,
      title: 'Shipping Details',
      buttonLabel: 'Checkout',
    });
  }

  steps.push({
    name: STEP_NAMES.CONFIRMATION,
    component: STEP_COMPONENTS.Confirmation,
    title: 'Checkout',
    buttonLabel: 'Continue',
  });

  return steps;
};

// Helper to find step index by name
const findStepIndexByName = (steps, stepName) => {
  const index = steps.findIndex((step) => step.name === stepName);
  return index >= 0 ? index : 0; // Default to first step if not found
};

// Helper to get step name by index
const getStepNameByIndex = (steps, index) => {
  return steps[index]?.name || STEP_NAMES.TOKEN;
};

export const GiftFormProvider = ({
  children,
  schema = giftFormSchema,
  mode = 'all',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useForm({
    resolver: yupResolver(schema),
    mode,
    defaultValues: {
      ...defaultInitialValues,
    },
  });

  const privyToken = useSelector(selectPrivyToken);
  const isAuthenticated = !!privyToken;
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const userEmail = currentUser?.email;

  const {
    watch,
    getValues,
    setValue,
    clearErrors,
    trigger,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    control,
  } = form;

  const [selectedPaymentToken, setSelectedPaymentToken] = useState(null);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState(null); // 'token', 'applePay'
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [claimUrl, setClaimUrl] = useState(null);
  const [fromUsdcDirection, setFromUsdcDirection] = useState(true);
  const [gift, setGift] = useState(null);
  const [giftId, setGiftId] = useState(null);
  const [payingWith, setPayingWith] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // API hooks for gift creation
  const [createGiftMutation] = useCreateGiftMutation();
  const [createTrialGiftMutation] = useCreateTrialGiftMutation();
  const [updateGiftMutation] = useUpdateGiftMutation();
  const [prepareTransactions] = usePrepareTransactionsMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();

  // Solana utilities for transaction signing
  const {
    signAndSerializeTransaction,
    parseSolanaError,
    refreshTokenBalances,
  } = useSolana();

  // Watch form values first (needed for selectors and hooks below)
  const isUsingUSDC = watch('isUsingUSDC');
  const delivery = watch('delivery');
  const email = watch('email');
  const message = watch('message');
  const senderName = watch('senderName');
  const recipientName = watch('recipientName');
  const image = watch('image');
  const targetTokenAddress = watch('targetTokenAddress');
  const network = watch('network');
  const shippingAddressId = watch('shippingAddressId');
  const usdAmount = watch('usdAmount');
  const targetTokenAmount = watch('targetTokenAmount');
  const addressLine1 = watch('addressLine1');
  const addressLine2 = watch('addressLine2');
  const city = watch('city');
  const state = watch('state');
  const postalCode = watch('postalCode');
  const country = watch('country');
  const formattedAddress = watch('formattedAddress');

  // Get tokens from Redux (now network and targetTokenAddress are defined)
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, network || 'solana', 'USDC'),
  );

  const targetToken = useSelector((state) =>
    targetTokenAddress && network
      ? selectTokenByNetworkAndAddress(state, network, targetTokenAddress)
      : null,
  );

  // Get triggerCreateOrder from useGiftAmountCalculations hook
  // We need this for calculating baseTokenAmount when paying with non-USDC tokens
  const { triggerCreateOrder } = useGiftAmountCalculations({
    network,
    targetTokenAddress,
    targetTokenAmount,
    usdAmount,
    fromUsdcDirection,
    delivery,
    selectedPaymentMethodType,
  });

  useEffect(() => {
    if (userEmail && setValue) {
      setValue('userEmail', userEmail);
    }
  }, [userEmail, setValue]);

  useEffect(() => {
    // Clear errors when delivery method changes away from requiring those fields
    // Don't trigger validation immediately - let it happen on user interaction
    if (delivery !== 'email') {
      clearErrors('email');
    }
    if (delivery !== 'mail') {
      clearErrors('shippingAddressId');
    }
  }, [delivery, clearErrors]);

  useEffect(() => {
    trigger('shippingAddressId');
  }, [shippingAddressId]);

  // Read step, trial flag, and giftId from URL
  const urlStepName = useMemo(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      return stepParam;
    }
    // If no step in URL, check if token is in URL params - if so, skip to customize
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');
    return tokenAddress && networkParam
      ? STEP_NAMES.CUSTOMIZE
      : STEP_NAMES.TOKEN;
  }, [searchParams]);

  const isTrialFlow = useMemo(
    () => searchParams.get('trial') === '1',
    [searchParams],
  );

  const giftIdFromUrl = useMemo(
    () => searchParams.get('giftId'),
    [searchParams],
  );
  // URL is the source of truth for giftId
  const currentGiftId = giftIdFromUrl || giftId;

  // Fetch gift data if we have a giftId from URL
  const {
    data: giftData,
    isLoading: isLoadingGift,
    isFetching: isFetchingGift,
  } = useGetGiftQuery(currentGiftId, {
    skip: !currentGiftId || !isAuthenticated,
  });

  const fetchedGift = giftData?.gift;

  // Lock in the initial authentication state to keep steps array stable
  const initialAuthStateRef = useRef(isAuthenticated);
  if (initialAuthStateRef.current === undefined) {
    initialAuthStateRef.current = isAuthenticated;
  }
  const token = useSelector(selectPrivyToken);

  const { data: gifts } = useGetGiftsQuery({
    skip: !token,
  });
  const showFreeGiftFlag = useFlag('showFreeGift');
  const showFreeGift =
    showFreeGiftFlag && (gifts?.sentGifts?.length === 0 || !gifts?.sentGifts);
  const [isElegible, setIsElegible] = useState(false);
  const [reason, setReason] = useState(null);
  const freeGiftAllInputs = useFlag('freeGiftFullInputs');

  // Only show the trial / free-gift experience when:
  // - The user entered via a trial flow (URL param), AND
  // - The remote flag says they are in the cohort for a free gift.
  const showTrialExperience = isTrialFlow && showFreeGift;

  // Build steps array using locked initial auth state - stays stable throughout flow.
  // Sender Info is only shown for flows that STARTED logged out (progressive onboarding).
  const steps = useMemo(
    () =>
      buildSteps(
        delivery,
        initialAuthStateRef.current,
        showTrialExperience,
        freeGiftAllInputs,
      ),
    [delivery, showTrialExperience, freeGiftAllInputs],
  );

  // Convert URL step name to index - URL is the source of truth
  const currentStepIndex = useMemo(() => {
    if (!urlStepName) {
      return 0;
    }
    const index = findStepIndexByName(steps, urlStepName);
    // If step not found, default to first step
    return index >= 0 ? index : 0;
  }, [urlStepName, steps]);

  // Special case: if sending or succeeded, always show confirmation (last step)
  const effectiveStepIndex = useMemo(() => {
    if (sendingStatus === 'sending' || sendingStatus === 'success') {
      return steps.length - 1;
    }
    return currentStepIndex;
  }, [currentStepIndex, sendingStatus, steps.length]);

  // Get step name for the current step
  const currentStepName = getStepNameByIndex(steps, effectiveStepIndex);

  // Track when free gift flow is shown
  useEffect(() => {
    if (showTrialExperience) {
      sendEvent(EVENT_FREE_GIFT_FLOW_SHOWN);
    }
  }, [showTrialExperience]);

  useEffect(() => {
    if (showTrialExperience) {
      if (currentStepIndex === 0) {
        setIsElegible(true);
        setReason(null);
      } else if (currentStepIndex === 1) {
        if (delivery === 'link') {
          setIsElegible(true);
          setReason(null);
        } else {
          setIsElegible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      } else if (currentStepIndex === 2 || currentStepIndex === 3) {
        if (delivery === 'link') {
          if (!isUsingUSDC) {
            setIsElegible(false);
            setReason('Only USDC is eligible for free gifts');
          } else {
            if (usdAmount !== '1') {
              setIsElegible(false);
              setReason('Amount must be 1 to receive a free gift');
            } else {
              setIsElegible(true);
              setReason(null);
            }
          }
        } else {
          setIsElegible(false);
          if (delivery === 'mail') {
            setReason('Physical delivery is not eligible for free gifts');
          } else {
            setReason('Email delivery is not eligible for free gifts');
          }
        }
      }
    }
  }, [showTrialExperience, isUsingUSDC, usdAmount, delivery, currentStepIndex]);

  /**
   * Sync form values from loaded gift data
   */
  const syncFormWithGift = useCallback(
    (giftData) => {
      if (!giftData) return;
    // Map gift API data to form fields
    const formData = {
      network: giftData.network,
      targetTokenAddress: giftData.token,
      delivery: giftData.delivery || 'link',
      email: giftData.email,
      message: giftData.message,
      image: giftData.imageUrl || giftData.image,
      senderName: giftData.senderName,
      recipientName: giftData.recipientName,
      addressLine1: giftData.addressLine1,
      addressLine2: giftData.addressLine2,
      city: giftData.city,
      state: giftData.state,
      postalCode: giftData.postalCode,
      country: giftData.country,
      formattedAddress: giftData.formattedAddress,
      usdAmount: giftData.purchaseUsdValue ?? undefined,
    };

      // Set each field individually to trigger validation
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          setValue(key, value, { shouldValidate: false, shouldDirty: false });
        }
      });

      // Update giftId and gift state
      if (giftData.id) {
        setGiftId(giftData.id);
        setGift(giftData);
      }
    },
    [setValue],
  );

  /**
   * Sync form values from URL params (tokenAddress, network)
   * Only syncs FROM URL TO form when URL params exist (deep link scenario)
   * Never clears form values - only sets them if URL params are present
   */
  const syncFormWithUrlParams = useCallback(() => {
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');
    const formValues = getValues();

    // Only sync if URL params exist (deep link scenario)
    // If they don't exist, leave form values as-is (normal flow)
    if (tokenAddress && networkParam) {
      if (formValues?.targetTokenAddress !== tokenAddress) {
        setValue('targetTokenAddress', tokenAddress, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      if (formValues?.network !== networkParam) {
        setValue('network', networkParam, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
    // Explicitly do NOT clear values if URL params don't exist
    // Form values should persist across step transitions
  }, [searchParams, getValues, setValue]);

  const initializeForm = ({ initialValues }) => {
    setSelectedPaymentToken(null);
    setSelectedPaymentMethodType(null);
    setClaimUrl(null);
    setSendingStatus('idle');
    setFeedback(null);
    setFromUsdcDirection(true);
    form.reset({
      ...defaultInitialValues,
      ...initialValues,
      userEmail,
    });
    clearErrors();
  };

  /**
   * Auto-save gift as draft (creates or updates draft gift)
   * This is called automatically before showing confirmation step
   *
   * @returns {Promise<string>} The giftId of the created/updated draft
   */
  const autoSaveGift = useCallback(async () => {
    // Use USDC as default payment token for draft (will be updated on final submission)
    const paymentToken = usdcToken;

    if (!paymentToken?.address || !targetToken?.address) {
      throw new Error('Missing required tokens');
    }

    // For draft, use USD amount directly (simplified - no complex payment token calculations)
    const baseTokenAmount = toTokenAmount(
      usdAmount || '0',
      paymentToken.decimals,
    );

    if (!baseTokenAmount || baseTokenAmount === 0) {
      throw new Error('Unable to calculate payment amount. Please try again.');
    }

    // Prepare gift data (same structure as createGift)
    const giftData = {
      delivery: delivery,
      email: email,
      token: targetToken.address,
      baseToken: paymentToken.address,
      baseTokenAmount: baseTokenAmount,
      network: network,
      imageUrl: image,
      message: message,
    };

    if (delivery === 'mail') {
      giftData.addressLine1 = addressLine1;
      giftData.addressLine2 = addressLine2;
      giftData.city = city;
      giftData.state = state;
      giftData.postalCode = postalCode;
      giftData.senderName = senderName;
      giftData.country = country;
      giftData.recipientName = recipientName;
    }

    let createdGiftResult;
    let giftResponse;

    if (currentGiftId) {
      // Update existing draft
      giftResponse = await updateGiftMutation({
        giftId: currentGiftId,
        ...giftData,
      }).unwrap();
      createdGiftResult = giftResponse?.gift || { id: currentGiftId };
    } else {
      // Create new draft
      giftResponse = await createGiftMutation(giftData).unwrap();
      createdGiftResult = giftResponse?.gift;
      setGiftId(createdGiftResult.id);
      setGift(createdGiftResult);
    }

    // Update URL with giftId if we just created one
    if (!currentGiftId && createdGiftResult?.id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('giftId', createdGiftResult.id);
      setSearchParams(newParams, { replace: true });
    }

    return createdGiftResult.id;
  }, [
    usdcToken,
    targetToken,
    usdAmount,
    delivery,
    email,
    message,
    image,
    network,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
    senderName,
    country,
    recipientName,
    currentGiftId,
    createGiftMutation,
    updateGiftMutation,
    searchParams,
    setSearchParams,
    setGiftId,
    setGift,
  ]);

  /**
   * Create a gift with the current form data
   * This centralizes the gift creation logic that was previously in Confirmation component
   *
   * @param {Object} paymentTokenOverride - Optional token to override the selected payment token
   * @returns {Promise<Object>} The created gift object
   */
  const createGift = useCallback(
    async (paymentTokenOverride = null) => {
      // Determine the payment token to use
      const paymentToken =
        paymentTokenOverride ||
        (selectedPaymentMethodType === 'applePay'
          ? usdcToken
          : selectedPaymentToken);

      if (!paymentToken?.address || !targetToken?.address) {
        throw new Error('Missing required tokens');
      }

      // Calculate baseTokenAmount based on USD value or user-entered base token amount
      let baseTokenAmount;

      // Check if user entered amount in base token (payment token)
      // This happens when paymentToken === targetToken AND fromUsdcDirection === false
      if (paymentToken.address === targetToken.address && !fromUsdcDirection) {
        // User entered amount in base token directly - use it as-is
        baseTokenAmount = toTokenAmount(
          targetTokenAmount,
          paymentToken.decimals,
        );
      } else if (paymentToken.address === usdcToken.address) {
        // If paying with USDC, use usdAmount directly
        baseTokenAmount = toTokenAmount(usdAmount, paymentToken.decimals);
      } else {
        // For other tokens, get a quote for the USD value
        const orderResult = await triggerCreateOrder({
          inputMint: usdcToken.address,
          outputMint: paymentToken.address,
          amount: toTokenAmount(usdAmount, usdcToken.decimals),
        }).unwrap();

        baseTokenAmount = orderResult.outAmount;
      }

      // Validate baseTokenAmount is set
      if (!baseTokenAmount || baseTokenAmount === 0) {
        throw new Error(
          'Unable to calculate payment amount. Please try again.',
        );
      }

      // Prepare gift data
      const giftData = {
        delivery: delivery,
        email: email,
        token: targetToken.address, // Output token (what receiver gets)
        baseToken: paymentToken.address, // Input token (what creator pays with)
        baseTokenAmount: baseTokenAmount, // Amount in baseToken (required by backend)
        network: network,
        imageUrl: image,
        message: message,
      };

      if (delivery === 'mail') {
        giftData.addressLine1 = addressLine1;
        giftData.addressLine2 = addressLine2;
        giftData.city = city;
        giftData.state = state;
        giftData.postalCode = postalCode;
        giftData.senderName = senderName;
        giftData.country = country;
        giftData.recipientName = recipientName;
      }

      // Create or update gift (if giftId exists, update; otherwise create)
      let createdGift;
      let giftResponse;
      if (giftId) {
        giftResponse = await updateGiftMutation({
          giftId,
          ...giftData,
        }).unwrap();
        createdGift = giftResponse?.gift || { id: giftId };
      } else {
        giftResponse = await createGiftMutation(giftData).unwrap();
        createdGift = giftResponse?.gift;
        setGiftId(createdGift.id);
      }

      // Update gift state
      setGift(createdGift);

      return createdGift;
    },
    [
      selectedPaymentMethodType,
      selectedPaymentToken,
      usdcToken,
      targetToken,
      fromUsdcDirection,
      targetTokenAmount,
      usdAmount,
      delivery,
      email,
      message,
      image,
      network,
      addressLine1,
      addressLine2,
      city,
      state,
      postalCode,
      senderName,
      country,
      recipientName,
      triggerCreateOrder,
      createGiftMutation,
      updateGiftMutation,
      giftId,
    ],
  );

  const createTrialGift = useCallback(async () => {
    const session = await getVerisoulSession();
    const sessionId = session?.session_id;
    const giftData = {
      token: targetToken.address,
      network: network,
      imageUrl: image,
      message: message,
      session_id: sessionId,
    };

    const giftResponse = await createTrialGiftMutation(giftData).unwrap();
    setGiftId(giftResponse.gift.id);

    setGift(giftResponse.gift);

    return giftResponse;
  }, [targetToken, message, image, network]);

  /**
   * Submit gift - handles the full flow: create → prepare → sign → broadcast
   * This centralizes the transaction flow logic that was previously in Confirmation component
   */
  const submitGift = useCallback(async () => {
    sendEvent(EVENT_GIFT_FLOW_CREATE_GIFT_BUTTON_CLICKED, {
      is_free_gift: showTrialExperience,
    });
    try {
      setSendingStatus('sending');
      setFeedback('Creating gift...');

      // Step 1: Create or update gift (if giftId exists, it's an update; otherwise create)
      let createdGift;

      // Trial / free-gift flow:
      // - Only allowed when we are in a trial flow (URL param) AND
      //   the cohort flag is on AND our local eligibility checks pass.
      if (isTrialFlow) {
        if (showTrialExperience && isElegible) {
          try {
            const trialResult = await createTrialGift();
            const trialGift = trialResult?.gift || trialResult;

            setSendingStatus('success');
            setFeedback(null);
            setClaimUrl(trialResult?.claimUrl || trialGift?.claimUrl || null);
            return;
          } catch (err) {
            setSendingStatus('idle');
            setFeedback(
              reason || 'Trial gift is not available for your account.',
            );
            return;
          }
        } else {
          // In a trial flow, but trial experience is not available or not eligible.
          // Do not create any gift here.
          setSendingStatus('idle');
          setFeedback(
            reason || 'Trial gift is not available for your account.',
          );
          return;
        }
      }

      // Normal paid gift path:
      // - Only when this is NOT a trial flow.
      createdGift = await createGift();

      // Step 2: Prepare transactions (unified endpoint)
      setFeedback('Preparing transactions...');
      const prepareResult = await prepareTransactions(createdGift.id).unwrap();

      // Step 3: Sign and broadcast (unified endpoint)
      setFeedback('Please sign transaction...');

      if (prepareResult.swapTransaction) {
        // Gift with swap - sign both transactions
        const signedSwapTx = await signAndSerializeTransaction(
          prepareResult.swapTransaction,
        );

        const signedSecondTx = await signAndSerializeTransaction(
          prepareResult.htlcTransaction || prepareResult.transferTransaction,
        );

        setFeedback('Broadcasting transactions...');

        // Unified endpoint handles all gift types with swap
        const result = await broadcastAndTrack({
          giftId: createdGift.id,
          serializedTransaction: signedSwapTx, // Swap transaction
          serializedSecondTransaction: signedSecondTx, // HTLC or transfer transaction
          requestId: prepareResult.requestId, // Jupiter Ultra request ID
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      } else {
        // Gift without swap - single transaction
        const signedTx = await signAndSerializeTransaction(
          prepareResult.serializedTransaction,
        );

        setFeedback('Broadcasting transaction...');

        // Unified endpoint handles all gift types without swap
        const result = await broadcastAndTrack({
          giftId: createdGift.id,
          serializedTransaction: signedTx,
        }).unwrap();

        setClaimUrl(result.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      }

      sendEvent(EVENT_GIFT_FLOW_GIFT_CREATED, {
        id: createdGift.id,
        token: createdGift.token,
        base_token: createdGift.baseToken,
        base_token_amount: createdGift.baseTokenAmount,
        created_date: new Date().toISOString(),
        is_free_gift: showTrialExperience,
      });

      refreshTokenBalances();
      hapticsImpactMedium();
    } catch (error) {
      setSendingStatus('error');
      const errorMessage = parseSolanaError(error);
      sendEvent(EVENT_GIFT_FLOW_GIFT_FAILED, {
        error: errorMessage,
        is_free_gift: showTrialExperience,
      });
      setFeedback(`Error: ${errorMessage}`);
      throw error; // Re-throw so component can handle if needed
    }
  }, [
    createGift,
    prepareTransactions,
    broadcastAndTrack,
    signAndSerializeTransaction,
    parseSolanaError,
    refreshTokenBalances,
    giftId,
    isTrialFlow,
    showFreeGift,
    showTrialExperience,
    isElegible,
    reason,
    createTrialGift,
  ]);

  // Sync form with URL params when they change
  // Only sync when URL actually has tokenAddress/network params (deep link scenario)
  // Don't sync on every step transition - that would clear form values unnecessarily
  useEffect(() => {
    const tokenAddress = searchParams.get('tokenAddress');
    const networkParam = searchParams.get('network');

      // Only sync if URL params exist (deep link scenario)
      // If they don't exist, we're in normal flow and form values should persist
      if (tokenAddress && networkParam) {
        syncFormWithUrlParams();
      }
  }, [syncFormWithUrlParams, searchParams]);

  // Sync form with fetched gift data when it loads (only once per giftId)
  const syncedGiftIdRef = useRef(null);
  useEffect(() => {
    if (
      fetchedGift?.id &&
      fetchedGift.id === currentGiftId &&
      syncedGiftIdRef.current !== currentGiftId
    ) {
      syncFormWithGift(fetchedGift);
      syncedGiftIdRef.current = currentGiftId;
    }
    // Reset ref if giftId changes to something else
    if (currentGiftId && currentGiftId !== syncedGiftIdRef.current) {
      syncedGiftIdRef.current = null;
    }
  }, [fetchedGift, currentGiftId, syncFormWithGift]);

  // Autosave before showing confirmation step when we don't have giftId
  useEffect(() => {
    const handleAutosave = async () => {
      // Only for confirmation step
      if (currentStepName !== STEP_NAMES.CONFIRMATION) {
        return;
      }

      // Skip autosave for trial flows - they don't create drafts
      if (isTrialFlow) {
        return;
      }

      // If we already have a giftId, we're good
      if (currentGiftId) {
        return;
      }

      // If not authenticated, can't autosave
      if (!isAuthenticated) {
        return;
      }

      // Check if we have minimum required data for gift creation
      const formValues = getValues();
      if (
        !formValues?.targetTokenAddress ||
        !formValues?.network ||
        !formValues?.usdAmount
      ) {
        return;
      }

      // Trigger autosave
      setIsAutoSaving(true);

      try {
        const savedId = await autoSaveGift();
        console.log('[GiftFormContext] Autosave successful, giftId:', savedId);
      } catch (error) {
        console.error('[GiftFormContext] Failed to autosave gift:', error);
        // Don't block - let user continue (they can retry)
      } finally {
        setIsAutoSaving(false);
      }
    };

    handleAutosave();
  }, [
    currentStepName,
    currentGiftId,
    isAuthenticated,
    isTrialFlow,
    autoSaveGift,
    getValues,
  ]);

  // Calculate step component and loading state
  const currentStepConfig = steps[effectiveStepIndex];
  let Component = currentStepConfig?.component || STEP_COMPONENTS.TokenSelector;
  const isConfirmationStep =
    currentStepConfig?.name === STEP_NAMES.CONFIRMATION;

  if (isConfirmationStep) {
    // Show a skeleton that matches the Confirmation screen while loading necessary data
    const isConfirmationLoading =
      sendingStatus === 'idle' &&
      (
        isAutoSaving ||
        (currentGiftId && !fetchedGift && (isLoadingGift || isFetchingGift))
      );

    if (isConfirmationLoading) {
      Component = ConfirmationSkeleton;
    }
  }

  const currentTitle = currentStepConfig?.title || '';
  const buttonLabel = currentStepConfig?.buttonLabel || 'Continue';

  const shouldShowBackButton =
    effectiveStepIndex > 0 &&
    (sendingStatus === 'idle' || effectiveStepIndex < steps.length - 1);

  // Get previous step name for back navigation
  let previousStepName = null;
  if (effectiveStepIndex > 0) {
    let prevIdx = effectiveStepIndex - 1;
    // When authenticated, skip SenderInfo when navigating backwards
    while (prevIdx >= 0) {
      const stepName = steps[prevIdx]?.name;
      if (stepName === STEP_NAMES.SENDER_INFO && isAuthenticated) {
        prevIdx -= 1;
        continue;
      }
      previousStepName = getStepNameByIndex(steps, prevIdx);
      break;
    }
  }

  // Get next step name for continue navigation
  let nextStepName = null;
  if (effectiveStepIndex < steps.length - 1) {
    let nextIdx = effectiveStepIndex + 1;
    // When authenticated, skip SenderInfo when navigating forwards
    while (nextIdx < steps.length) {
      const stepName = steps[nextIdx]?.name;
      if (stepName === STEP_NAMES.SENDER_INFO && isAuthenticated) {
        nextIdx += 1;
        continue;
      }
      nextStepName = getStepNameByIndex(steps, nextIdx);
      break;
    }
  }

  // Helper to update step in URL (for navigation)
  const updateStepInUrl = useCallback(
    (stepName) => {
      const newParams = new URLSearchParams(searchParams);
      if (stepName === STEP_NAMES.TOKEN) {
        newParams.delete('step');
      } else {
        newParams.set('step', stepName);
      }
      // Preserve giftId if it exists
      if (currentGiftId) {
        newParams.set('giftId', currentGiftId);
      }
      setSearchParams(newParams, { replace: false });
    },
    [searchParams, setSearchParams, currentGiftId],
  );

  // If the user has become authenticated, they should not see SenderInfo anymore.
  // When on SenderInfo and auth flips to true (e.g., progressive onboarding),
  // immediately advance to the next step so navigating back/forward never shows it.
  useEffect(() => {
    if (isAuthenticated && currentStepName === STEP_NAMES.SENDER_INFO && nextStepName) {
      updateStepInUrl(nextStepName);
    }
  }, [isAuthenticated, currentStepName, nextStepName, updateStepInUrl]);

  // Guard: If user reaches Confirmation step without token/network, redirect to Token step.
  // This handles deep-links or edge cases where form state is missing.
  useEffect(() => {
    if (currentStepName === STEP_NAMES.CONFIRMATION) {
      // If we have a giftId, we'll load the gift data (which has token/network)
      // so no redirect needed in that case.
      if (currentGiftId) {
        return;
      }

      // If we don't have token/network in form, redirect to Token step
      if (!targetTokenAddress || !network) {
        updateStepInUrl(STEP_NAMES.TOKEN);
      }
    }
  }, [currentStepName, currentGiftId, targetTokenAddress, network, updateStepInUrl]);

  return (
    <GiftFormContext.Provider
      value={{
        handleSubmit,
        control,
        errors,
        isValid,
        isSubmitting,
        setValue,
        getValues,
        claimUrl,
        gift,
        setGift,
        setFeedback,
        setClaimUrl,
        setSendingStatus,
        clearErrors,
        trigger,
        payingWith,
        setPayingWith,
        isUsingUSDC,
        delivery,
        email,
        message,
        image,
        network,
        sendingStatus,
        feedback,
        usdAmount,
        selectedPaymentToken,
        setSelectedPaymentToken,
        selectedPaymentMethodType,
        setSelectedPaymentMethodType,
        targetTokenAddress,
        targetTokenAmount,
        initializeForm,
        setIsUsingUSDC: (newValue) => setValue('isUsingUSDC', newValue),
        fromUsdcDirection,
        senderName,
        setFromUsdcDirection,
        addressLine1,
        addressLine2,
        city,
        recipientName,
        state,
        postalCode,
        country,
        formattedAddress,
        // Gift creation functions
        createGift,
        submitGift,
        // Gift ID state
        giftId,
        setGiftId,
        // Expose tokens for components that need them
        usdcToken,
        targetToken,
        // Navigation and step state
        currentStep: effectiveStepIndex,
        currentStepName,
        totalSteps: steps.length,
        currentTitle,
        Component,
        onBackStepName: previousStepName,
        onNextStepName: nextStepName,
        shouldShowBackButton,
        buttonLabel,
        updateStepInUrl,
        // Gift data from API
        fetchedGift,
        isLoadingGift: isLoadingGift || isFetchingGift,
        isAutoSaving,
        // Sync functions
        syncFormWithGift,
        syncFormWithUrlParams,
        isElegible,
        reason,
        showFreeGift: showTrialExperience,
      }}
    >
      {children}
    </GiftFormContext.Provider>
  );
};

export const useGiftForm = () => {
  const ctx = useContext(GiftFormContext);
  if (!ctx)
    throw new Error('useGiftForm must be used within a GiftFormProvider');
  return ctx;
};

export { giftFormSchema, GiftFormContext };
